/* eslint-disable react-hooks/exhaustive-deps */
import { Form, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../button/button'
import { Icon } from '../icon/icon'
import Text from '../text/text'
import CategorySelectionSheet from './category-selection-sheet'
import HeaderSearchDate from './header-search-date/header-search-date'
import HeaderSearchPlaces from './header-search-places'
import HeaderSearchText from './header-search-text'
import { useCategories } from '~/utils/categories'
import {
	getCountryCodeByLanguage,
	getCountryName,
	useClientLang,
} from '~/utils/i18n.utils'
import {
	isMapRoute,
	isPlaceRoute,
} from '~/utils/location/location.route.helpers'
import { useUserLocation } from '~/utils/location/location'
import { cn } from '~/utils/misc'
import { useUserQueries } from '~/utils/queries/userUrlQueries'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '../ui/sheet'

interface SearchSheetProps {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	trigger: React.ReactNode
	selectedSection?: number // 0 = search, 1 = location, 2 = dates
}

export function SearchSheet({
	isOpen,
	onOpenChange,
	trigger,
	selectedSection = 0,
}: SearchSheetProps) {
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams] = useSearchParams()

	const locationData = useUserLocation()
	const userLocation = locationData?.userLocation
	const countries = locationData?.countries
	const cities = locationData?.cities

	const queries = useUserQueries()
	const allCategories = useCategories() ?? []

	const { t } = useTranslation()
	const lang = useClientLang().lngShort

	const isMapPage = isMapRoute(location.pathname)

	const isPlacePage = isPlaceRoute(location.pathname)

	const codeByLanguage = getCountryCodeByLanguage(lang)

	const locationQuery =
		userLocation?.city ??
		(userLocation?.country
			? getCountryName(userLocation?.country, lang)
			: undefined) ??
		(isMapPage
			? t('public_map_area')
			: !isPlacePage
				? getCountryName(codeByLanguage, lang)
				: undefined)

	const [categories, setCategories] = useState<string[]>(queries.categories)
	const [fromDate, setFromDate] = useState<Date | null>(
		queries.fromDate ?? null,
	)
	const [toDate, setToDate] = useState<Date | null>(queries.toDate ?? null)
	const [countryCode, setCountryCode] = useState<string | undefined>(
		userLocation?.countryCode?.toLowerCase(),
	)
	const [city, setCity] = useState<string | undefined>(
		userLocation?.city?.toLowerCase(),
	)
	const [searchText, setSearchText] = useState<string | undefined>(
		queries.searchText,
	)

	// Accordion state management
	const [openAccordions, setOpenAccordions] = useState<number[]>([])
	// Category sheet state
	const [isCategorySheetOpen, setIsCategorySheetOpen] = useState(false)

	// Set initial accordion based on selectedSection
	useEffect(() => {
		if (isOpen && selectedSection !== undefined) {
			setOpenAccordions([selectedSection])
		}
	}, [isOpen, selectedSection])

	const toggleAccordion = (index: number) => {
		setOpenAccordions(prev =>
			prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index],
		)
	}

	useEffect(() => {
		setCategories(queries.categories)

		if (queries.fromDate) {
			setFromDate(queries.fromDate)
		} else {
			setFromDate(null)
		}
		if (userLocation?.countryCode) {
			setCountryCode(userLocation?.countryCode.toLowerCase())
		} else {
			setCountryCode(undefined)
		}
		if (userLocation?.city) {
			setCity(userLocation?.city.toLowerCase())
		} else {
			setCity(undefined)
		}
		if (queries.toDate) {
			setToDate(queries.toDate)
		} else {
			setToDate(null)
		}

		setSearchText(queries.searchText)
	}, [
		queries.categories,
		locationQuery,
		queries.fromDate,
		queries.toDate,
		queries.searchText,
	])

	const search = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const newSearchParams = new URLSearchParams(searchParams.toString())
		if (categories?.length > 0) {
			newSearchParams.set('categories', categories.join('|'))
		} else {
			newSearchParams.delete('categories')
		}
		if (fromDate) {
			newSearchParams.set('from', fromDate.toLocaleDateString('sv-SE'))
		} else {
			newSearchParams.delete('from')
		}
		if (toDate) {
			newSearchParams.set('to', toDate.toLocaleDateString('sv-SE'))
		} else {
			newSearchParams.delete('to')
		}
		if (searchText) {
			newSearchParams.set('s', searchText)
		} else {
			newSearchParams.delete('s')
		}

		if (isMapPage && !countryCode && !city) {
			// Stay on map page, just update search params
			if (countryCode) {
				newSearchParams.set('country', countryCode)
			} else {
				newSearchParams.delete('country')
			}
			if (city) {
				newSearchParams.set('city', city)
			} else {
				newSearchParams.delete('city')
			}
			// Remove map bounds when changing location to trigger new search
			if (countryCode || city) {
				newSearchParams.delete('swLat')
				newSearchParams.delete('swLng')
				newSearchParams.delete('neLat')
				newSearchParams.delete('neLng')
			}
			navigate({
				search: newSearchParams.toString(),
			})
		} else {
			if (countryCode) {
				newSearchParams.delete('nearby')
				newSearchParams.delete('swLat')
				newSearchParams.delete('swLng')
				newSearchParams.delete('neLat')
				newSearchParams.delete('neLng')
			}
			const path =
				countryCode && city
					? `${lang === 'sv' ? '' : `/${lang}`}/place/${countryCode}/${city}`
					: countryCode
						? `${lang === 'sv' ? '' : `/${lang}`}/place/${countryCode}`
						: `${lang === 'sv' ? '' : `/${lang}`}/place`
			navigate({
				pathname: path,
				search: newSearchParams.toString(),
			})
		}

		onOpenChange(false)
	}

	const clearSearch = () => {
		setCategories([])
		setSearchText(undefined)
		setFromDate(null)
		setToDate(null)
		setCountryCode(undefined)
		setCity(undefined)
		if (isMapPage) {
			const swLat = searchParams.get('swLat')
			const swLng = searchParams.get('swLng')
			const neLat = searchParams.get('neLat')
			const neLng = searchParams.get('neLng')
			const newSearchParams = new URLSearchParams()

			if (swLat && swLng && neLat && neLng) {
				newSearchParams.set('swLat', parseFloat(swLat).toFixed(8))
				newSearchParams.set('swLng', parseFloat(swLng).toFixed(8))
				newSearchParams.set('neLat', parseFloat(neLat).toFixed(8))
				newSearchParams.set('neLng', parseFloat(neLng).toFixed(8))
			}
		} else {
			navigate({
				pathname: `${lang === 'sv' ? '' : `/${lang}`}`,
			})
		}
		onOpenChange(false)
	}

	// Check if sections have active filters
	const hasLocationFilters = countryCode !== undefined || city !== undefined
	const hasDateFilters = fromDate !== null || toDate !== null

	return (
		<Sheet open={isOpen} onOpenChange={onOpenChange}>
			<SheetTrigger asChild>{trigger}</SheetTrigger>
			<SheetContent className="flex h-full w-full flex-col md:w-lg">
				<SheetHeader className="flex-shrink-0">
					<SheetTitle>{t('action_search_and_filter')}</SheetTitle>
					<SheetDescription>
						{t('action_customize_your_search')}
					</SheetDescription>
				</SheetHeader>

				<div className="flex-1 overflow-y-auto">
					<Form id="search-form" onSubmit={search} className="flex flex-col gap-4 p-4">
						{/* Search field at the top */}
						<div className="mb-4">
							<HeaderSearchText
								searchText={searchText}
								setSearchText={setSearchText}
							/>
						</div>

						<div className="space-y-2">
							{/* Categories Button */}
							<div className="border-foreground/20 rounded-lg border">
								<button
									type="button"
									className={`hover:bg-foreground/5 flex w-full items-center justify-between p-4 text-left transition-colors ${
										categories.length > 0 ? 'bg-secondary/20' : ''
									}`}
									onClick={() => setIsCategorySheetOpen(true)}
								>
									<div className="flex items-center gap-2">
										<Text className="font-medium">{t('misc_categories')}</Text>
										{categories.length > 0 && (
											<div className="bg-secondary h-2 w-2 rounded-full" />
										)}
									</div>
									<Icon
										name="arrow-right"
										size="sm"
										className="transition-transform duration-200"
									/>
								</button>
							</div>

							{/* Location Accordion */}
							<AccordionSection
								title={t('misc_place')}
								isOpen={openAccordions.includes(1)}
								onToggle={() => toggleAccordion(1)}
								hasActiveFilters={hasLocationFilters}
							>
								<HeaderSearchPlaces
									countryCode={countryCode}
									city={city}
									setCountry={setCountryCode}
									setCity={setCity}
									countries={countries}
									cities={cities}
								/>
							</AccordionSection>

							{/* Date Accordion */}
							<AccordionSection
								title={t('misc_dates')}
								isOpen={openAccordions.includes(2)}
								onToggle={() => toggleAccordion(2)}
								hasActiveFilters={hasDateFilters}
							>
								<HeaderSearchDate
									fromDate={fromDate ? new Date(fromDate) : null}
									toDate={toDate ? new Date(toDate) : null}
									setFromDate={setFromDate}
									setToDate={setToDate}
								/>
							</AccordionSection>
						</div>

					</Form>
				</div>

				{/* Action buttons */}
				<div className="flex-shrink-0 border-t p-4">
					<div className="flex justify-between gap-4">
						<Button
							type="button"
							onClick={clearSearch}
							variant="outline-light"
							size="lg"
						>
							{t('action_clear_search')}
						</Button>
						<Button
							type="submit"
							form="search-form"
							variant="secondary"
							size="lg"
						>
							{t('action_search')}
						</Button>
					</div>
				</div>
			</SheetContent>

			{/* Category Selection Sheet */}
			<CategorySelectionSheet
				categories={categories}
				setCategories={setCategories}
				allCategories={allCategories}
				isOpen={isCategorySheetOpen}
				onOpenChange={setIsCategorySheetOpen}
				trigger={<div />} // Empty trigger since we open it programmatically
			/>
		</Sheet>
	)
}

function AccordionSection({
	title,
	isOpen,
	onToggle,
	hasActiveFilters,
	children,
}: {
	title: string
	isOpen: boolean
	onToggle: () => void
	hasActiveFilters?: boolean
	children: React.ReactNode
}) {
	return (
		<div className="border-foreground/20 rounded-lg border">
			<button
				type="button"
				className={cn(
					'hover:bg-foreground/5 flex w-full items-center justify-between p-4 text-left transition-colors',
					isOpen ? 'bg-foreground/5 border-foreground/20 border-b' : '',
					hasActiveFilters ? 'bg-secondary/20' : '',
				)}
				onClick={onToggle}
			>
				<div className="flex items-center gap-2">
					<Text className="font-medium">{title}</Text>
					{hasActiveFilters && (
						<div className="bg-secondary h-2 w-2 rounded-full" />
					)}
				</div>
				<Icon
					name="chevron-down"
					size="sm"
					className={cn(
						'transition-transform duration-200',
						isOpen ? 'rotate-180' : '',
					)}
				/>
			</button>
			{isOpen && <div className="p-4">{children}</div>}
		</div>
	)
}
